// Pure rules behind the VERI*FACTU behaviour of the invoice screens. No React,
// no Firebase: everything here is unit-tested and shared by the list, the edit
// page, the bulk generator and the saga.
import omit from 'lodash/omit';
import { CREDIT_NOTE_SERIES } from '../../../components/library/verifactu/status';
import DateFormatter from '../../../components/utility/DateFormatter';

export const REGISTERED_LOCK_MESSAGE =
  'Registered with the AEAT (VERI*FACTU). Registered invoices cannot be modified; issue a credit note instead.';

export const REGISTERED_DELETE_MESSAGE =
  'This invoice is registered with the AEAT (VERI*FACTU) and cannot be deleted. ' +
  'Issue a credit note, or cancel the AEAT record from the VERI*FACTU page if it was issued by mistake.';

// Shown when the state of VERI*FACTU could not be had (it is only unknown, and thus a blocker, in a
// browser that has seen VERI*FACTU live): an invoice must not be saved or numbered without the check.
export const VERIFACTU_UNCHECKED_MESSAGE = 'VERI*FACTU could not be checked. Try again in a moment.';

// Roles that see the AEAT status of an invoice (list column, view block).
const VERIFACTU_ROLES = ['superadmin', 'admin', 'accountant'];

export function canSeeVerifactu(role) {
  return VERIFACTU_ROLES.indexOf(role) !== -1;
}

/**
 * Does saving this edit turn a draft into a final invoice, or insert a final
 * invoice straight away? Those are the saves that create the AEAT obligation.
 */
export function isFinalizingSave({ typeInvoice, orderStatus, isNewInvoice, newNumberInvoice }) {
  return typeInvoice === 'invoice' && orderStatus === 'invoice' && !!(isNewInvoice || newNumberInvoice);
}

// ---- Preflight ---------------------------------------------------------------

function defaultSerie(invoice) {
  const serie = invoice.invoice_serie;
  if (!serie || String(serie) === '0') return invoice.type === 'invoice' ? '1' : '101';
  return String(serie);
}

/**
 * Items for the `preflight` action. A draft is sent as it will look once
 * finalized: final status, and a well-formed placeholder number (`<serie>/0000`
 * is never a real number, sequences start at 0001) instead of the "#id" draft
 * number, so the number checks do not fire on something that does not exist yet.
 */
export function buildPreflightItems(invoices) {
  return invoices.map((invoice) => {
    const data = omit(invoice, ['key', 'verifactu']);
    if (invoice.orderStatus === 'draft') {
      data.orderStatus = invoice.type;
      data.number = `${defaultSerie(invoice)}/0000`;
    }
    return { key: invoice.key == null ? null : invoice.key, data };
  });
}

/** Number as the list shows it (+ client and date) so the user can find the invoice. */
export function draftLabel(invoice) {
  const number = invoice.orderStatus === 'draft'
    ? `${invoice.invoice_serie || ''}/${invoice.number}`
    : invoice.number;
  const parts = [number];
  if (invoice.billTo) parts.push(invoice.billTo);
  if (typeof invoice.orderDate === 'number') parts.push(DateFormatter.getFormatString(invoice.orderDate, 'DD/MM/YYYY'));
  return parts.join(' · ');
}

/** Invoices with errors, as [{key, label, errors}], in the order they were checked. */
export function collectPreflightFailures(invoices, response) {
  const results = response && Array.isArray(response.results) ? response.results : [];
  const aligned = results.length === invoices.length;
  const resultFor = (invoice, index) => {
    if (invoice.key != null) {
      const byKey = results.find((r) => r && r.key === invoice.key);
      if (byKey) return byKey;
    }
    const byPosition = aligned ? results[index] : undefined;
    if (byPosition && (byPosition.key == null || invoice.key == null || byPosition.key === invoice.key)) return byPosition;
    return undefined;
  };
  const failures = [];
  invoices.forEach((invoice, index) => {
    const result = resultFor(invoice, index);
    const errors = result && Array.isArray(result.errors) ? result.errors : [];
    if (errors.length > 0) {
      failures.push({ key: invoice.key == null ? null : invoice.key, label: draftLabel(invoice), errors });
    }
  });
  return failures;
}

/**
 * Runs the `preflight` action over `invoices` in chunks (a monthly run is
 * 100-160 invoices and a callable payload is limited). `callFn(payload)` is
 * `callVerifactu('preflight', payload)`. 25 invoices per call keeps the payload far below
 * the callable limit even for invoices with hundreds of lines. A callable error is not caught: the
 * caller must refuse to continue.
 */
export async function runPreflight(callFn, invoices, { chunkSize = 25 } = {}) {
  if (!invoices || invoices.length === 0) return { live: true, failures: [] };
  const failures = [];
  let live = true;
  for (let i = 0; i < invoices.length; i += chunkSize) {
    const chunk = invoices.slice(i, i + chunkSize);
    const response = await callFn({ invoices: buildPreflightItems(chunk) });
    if (response && response.live === false) live = false;
    failures.push(...collectPreflightFailures(chunk, response));
  }
  return live ? { live: true, failures } : { live: false, failures: [] };
}

// ---- Credit notes: the invoice they rectify ----------------------------------

export function isCreditNoteSerie(invoice) {
  return !!invoice && invoice.type === 'invoice' && CREDIT_NOTE_SERIES.indexOf(String(invoice.invoice_serie)) !== -1;
}

/**
 * Sets one or both parts of `invoice.rectifies = {number, orderDate}` in place.
 * The field is optional and Firestore rejects `undefined`, so it is removed
 * when both parts are empty.
 */
export function applyRectifies(invoice, patch) {
  const current = invoice.rectifies || {};
  const number = 'number' in patch ? patch.number : current.number;
  const orderDate = 'orderDate' in patch ? patch.orderDate : current.orderDate;
  const text = number == null ? '' : String(number);
  const date = orderDate == null ? null : orderDate;
  if (!text && date === null) {
    delete invoice.rectifies;
  } else {
    invoice.rectifies = { number: text, orderDate: date };
  }
  return invoice;
}

function rectifiesParts(invoice) {
  const r = invoice.rectifies || {};
  const number = r.number == null ? '' : String(r.number).trim();
  const hasDate = typeof r.orderDate === 'number' && isFinite(r.orderDate);
  return { number, hasDate, orderDate: hasDate ? r.orderDate : null };
}

/** Message when only one of the two rectified-invoice fields is filled, else null. */
export function rectifiesError(invoice) {
  if (!isCreditNoteSerie(invoice) || !invoice.rectifies) return null;
  const { number, hasDate } = rectifiesParts(invoice);
  if ((number !== '') !== hasDate) {
    return 'Enter both the rectified invoice number and the rectified invoice date, or leave both empty.';
  }
  return null;
}

/** Last touch before saving: trims the number, drops the field outside credit-note series. */
export function prepareRectifiesForSave(invoice) {
  if (!invoice.rectifies) return invoice;
  if (!isCreditNoteSerie(invoice)) {
    delete invoice.rectifies;
    return invoice;
  }
  const { number, orderDate } = rectifiesParts(invoice);
  if (number === '' && orderDate === null) {
    delete invoice.rectifies;
  } else {
    invoice.rectifies = { number, orderDate };
  }
  return invoice;
}
